import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { Alert, Button, Card, Field, Select, Spinner, Textarea } from '../../components/ui';
import { type MessageKey, t } from '../../i18n';
import { api } from '../../lib/api';
import { errorMessage } from '../../lib/errors';
import { SuggestedArticles } from '../knowledge/ArticleLinks';
import { appLabels, type Nature, NATURES, natureName, useTicketForm } from './data';

/** Formulario del solicitante (guía 7.1): lo que le pasa, con sus palabras. IT lo clasifica después. */
export function NewTicketPage() {
  const form = useTicketForm();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  // El inicio del portal abre el formulario con el tipo ya elegido (?tipo=solicitud).
  const [params] = useSearchParams();
  const [nature, setNature] = useState<Nature>(() => (NATURES as readonly string[]).includes(params.get('tipo') ?? '') ? (params.get('tipo') as Nature) : 'incidencia');
  const [appId, setAppId] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ number: number }>('/api/tickets', {
        json: { title: title.trim(), description: description.trim(), nature, affectedAppId: appId || null },
      });
      await qc.invalidateQueries({ queryKey: ['tickets', 'lista'] });
      void navigate(`/tickets/${created.number}`, { state: { created: true } });
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  if (!form.data) return form.error ? <Alert>{errorMessage(form.error)}</Alert> : <Spinner label={t('comun.cargando')} />;

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/tickets" className="text-sm text-marca hover:underline">
          ← {t('tk.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('tk.nuevo')}</h1>
        <p className="text-sm text-slate-600">{t('tk.nuevo_ayuda')}</p>
      </div>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field id="tk-titulo" label={t('tk.campo_titulo')} required minLength={3} maxLength={200} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea
            id="tk-descripcion"
            label={t('tk.campo_descripcion')}
            required
            maxLength={10_000}
            rows={7}
            placeholder={t('tk.campo_descripcion_ayuda')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
          <SuggestedArticles text={title} />
          <fieldset className="space-y-2">
            <legend className="text-sm font-medium text-slate-700">{t('tk.campo_naturaleza')}</legend>
            <div className="grid gap-2 sm:grid-cols-3">
              {NATURES.map((n) => (
                <label
                  key={n}
                  className={`flex cursor-pointer flex-col gap-1 rounded-md border p-3 text-sm ${nature === n ? 'border-marca bg-marca/5' : 'border-slate-200 hover:bg-slate-50'}`}
                >
                  <span className="flex items-center gap-2 font-medium text-slate-900">
                    <input type="radio" name="tk-naturaleza" value={n} checked={nature === n} onChange={() => setNature(n)} className="accent-marca" />
                    {natureName(n)}
                  </span>
                  <span className="text-xs text-slate-600">{t(`tk.naturaleza_${n}_ayuda` as MessageKey)}</span>
                </label>
              ))}
            </div>
          </fieldset>
          {form.data.apps.length > 0 && (
            <Select id="tk-app" label={t('tk.campo_app')} value={appId} onChange={(e) => setAppId(e.target.value)}>
              <option value="">{t('tk.app_ninguna')}</option>
              {appLabels(form.data.apps).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </Select>
          )}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || title.trim().length < 3 || !description.trim()}>
              {t('tk.enviar')}
            </Button>
            <Link to="/tickets" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('comun.cancelar')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
